import {
  SpeakerWaveIcon as HeroSpeakerWaveIcon,
  SpeakerXMarkIcon as HeroSpeakerXMarkIcon,
} from "@heroicons/react/24/solid";
import { forwardRef, type SVGProps } from "react";

type AccessibleIconProps = SVGProps<SVGSVGElement> & {
  title?: string;
};

export type PlayingIconProps = SVGProps<SVGSVGElement> & {
  animated?: boolean;
};

export const PlayIcon = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
  ({ "aria-hidden": ariaHidden = true, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={ariaHidden}
        {...props}
      >
        <title>Play</title>
        <path
          d="M13.9312 5.29206C11.4731 3.89665 8.40047 5.63984 8.40002 8.43053V39.5688C8.40002 42.3596 11.4729 44.1051 13.9312 42.7101L41.3563 27.1395C43.8146 25.744 43.8146 22.2553 41.3563 20.8598L13.9312 5.29206Z"
          fill="currentColor"
        />
      </svg>
    );
  },
);
PlayIcon.displayName = "PlayIcon";

export const PlayingIcon = forwardRef<SVGSVGElement, PlayingIconProps>(
  (
    { animated = true, className, "aria-hidden": ariaHidden = true, ...props },
    ref,
  ) => {
    return (
      <svg
        ref={ref}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        aria-hidden={ariaHidden}
        focusable="false"
        {...props}
      >
        <title>Playing</title>
        <rect
          className={
            animated
              ? "origin-[50%_100%] animate-grow [animation-delay:0s] [transform-box:fill-box]"
              : undefined
          }
          x="5"
          y="10"
          width="3"
          height="8"
          fill="currentColor"
        />
        <rect
          className={
            animated
              ? "origin-[50%_100%] animate-grow [animation-delay:0.2s] [transform-box:fill-box]"
              : undefined
          }
          x="10.5"
          y="6"
          width="3"
          height="12"
          fill="currentColor"
        />
        <rect
          className={
            animated
              ? "origin-[50%_100%] animate-grow [animation-delay:0.4s] [transform-box:fill-box]"
              : undefined
          }
          x="16"
          y="10"
          width="3"
          height="8"
          fill="currentColor"
        />
      </svg>
    );
  },
);
PlayingIcon.displayName = "PlayingIcon";

export const ReplayTenIcon = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
  ({ "aria-hidden": ariaHidden = true, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width="25"
        height="25"
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={ariaHidden}
        {...props}
      >
        <title>Replay 10 seconds</title>
        <path
          d="M4.16383 28.5337V19.4906L2.16016 20.0382V18.2462L5.10103 16.9188H6.42604V28.5337H4.16383ZM13.233 28.7327C12.2096 28.7327 11.3263 28.4783 10.583 27.9695C9.83966 27.4608 9.26869 26.7582 8.87015 25.8622C8.48233 24.9552 8.28844 23.9099 8.28844 22.7262C8.28844 21.5425 8.48233 20.5028 8.87015 19.6068C9.26869 18.6998 9.83424 17.9917 10.5668 17.4829C11.3101 16.9741 12.1988 16.7197 13.233 16.7197C14.2671 16.7197 15.1504 16.9741 15.883 17.4829C16.6263 17.9917 17.1919 18.6998 17.5797 19.6068C17.9782 20.5028 18.1775 21.5425 18.1775 22.7262C18.1775 23.9099 17.9782 24.9552 17.5797 25.8622C17.1919 26.7582 16.6263 27.4608 15.883 27.9695C15.1504 28.4783 14.2671 28.7327 13.233 28.7327ZM13.233 26.725C13.7716 26.725 14.2455 26.5646 14.6549 26.2438C15.0643 25.9121 15.3821 25.4474 15.6083 24.8501C15.8453 24.2527 15.9638 23.5449 15.9638 22.7262C15.9638 21.8965 15.8453 21.1887 15.6083 20.6023C15.3821 20.005 15.0643 19.546 14.6549 19.2252C14.2455 18.9043 13.7716 18.744 13.233 18.744C12.6943 18.744 12.2204 18.9043 11.811 19.2252C11.4016 19.546 11.0785 20.005 10.8415 20.6023C10.6153 21.1887 10.5022 21.8965 10.5022 22.7262C10.5022 23.5449 10.6153 24.2527 10.8415 24.8501C11.0785 25.4474 11.4016 25.9121 11.811 26.2438C12.2204 26.5646 12.6943 26.725 13.233 26.725Z"
          fill="currentColor"
        />
        <path
          d="M16.4696 3.26172C12.9945 3.27497 9.6497 4.54619 7.04924 6.82809L6.53893 7.2993L5.81354 8.0247V5.01362C5.81354 4.26723 5.20884 3.66255 4.46245 3.66255C3.71607 3.66255 3.11139 4.26723 3.11139 5.01362V11.421C3.11181 12.167 3.71633 12.772 4.46245 12.772H10.8698C11.6157 12.7718 12.2204 12.1669 12.2209 11.421C12.2209 10.6747 11.616 10.0682 10.8698 10.0679H7.59281L8.43552 9.22523C10.597 7.14459 13.4786 5.97546 16.4794 5.96387C18.5864 5.96465 20.6459 6.59025 22.3979 7.76074C24.151 8.93219 25.5191 10.5973 26.326 12.5452C27.1328 14.4934 27.3435 16.638 26.932 18.7062C26.5206 20.7743 25.5059 22.6743 24.0149 24.1654C23.2392 24.941 22.3521 25.5871 21.3891 26.0873C20.7268 26.4312 20.4685 27.2473 20.8122 27.9097C21.1562 28.572 21.9721 28.8305 22.6344 28.4865C23.8419 27.8594 24.9527 27.0481 25.9251 26.0757C27.794 24.2066 29.0675 21.8262 29.5833 19.2341C30.0991 16.6416 29.8344 13.9529 28.8229 11.5109C27.8112 9.0691 26.0971 6.98259 23.8995 5.51416C21.7018 4.04581 19.1186 3.26172 16.4755 3.26172H16.4696Z"
          fill="currentColor"
        />
      </svg>
    );
  },
);
ReplayTenIcon.displayName = "ReplayTenIcon";

export const ForwardTenIcon = forwardRef<
  SVGSVGElement,
  SVGProps<SVGSVGElement>
>(({ "aria-hidden": ariaHidden = true, ...props }, ref) => {
  return (
    <svg
      ref={ref}
      width="26"
      height="27"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={ariaHidden}
      {...props}
    >
      <title>Forward 10 seconds</title>
      <path
        d="M15.8265 28.5426V19.4964L13.8228 20.0441V18.2515L16.7637 16.9236H18.0887V28.5426H15.8265ZM24.8956 28.7417C23.8723 28.7417 22.9889 28.4872 22.2457 27.9782C21.5023 27.4693 20.9313 26.7665 20.5329 25.8702C20.1451 24.9629 19.951 23.9172 19.951 22.733C19.951 21.5489 20.1451 20.5089 20.5329 19.6125C20.9313 18.7052 21.4969 17.9969 22.2295 17.488C22.9727 16.979 23.8615 16.7244 24.8956 16.7244C25.9297 16.7244 26.8131 16.979 27.5457 17.488C28.2889 17.9969 28.8545 18.7052 29.2423 19.6125C29.6409 20.5089 29.8402 21.5489 29.8402 22.733C29.8402 23.9172 29.6409 24.9629 29.2423 25.8702C28.8545 26.7665 28.2889 27.4693 27.5457 27.9782C26.8131 28.4872 25.9297 28.7417 24.8956 28.7417ZM24.8956 26.7332C25.4344 26.7332 25.9083 26.5728 26.3175 26.2519C26.727 25.9201 27.0447 25.4552 27.271 24.8577C27.5078 24.2601 27.6266 23.5521 27.6266 22.733C27.6266 21.9031 27.5078 21.195 27.271 20.6084C27.0447 20.0109 26.727 19.5517 26.3175 19.2308C25.9083 18.9098 25.4344 18.7494 24.8956 18.7494C24.357 18.7494 23.8831 18.9098 23.4736 19.2308C23.0642 19.5517 22.7411 20.0109 22.5042 20.6084C22.278 21.195 22.1649 21.9031 22.1649 22.733C22.1649 23.5521 22.278 24.2601 22.5042 24.8577C22.7411 25.4552 23.0642 25.9201 23.4736 26.2519C23.8831 26.5728 24.357 26.7332 24.8956 26.7332Z"
        fill="currentColor"
      />
      <path
        d="M15.5308 3.26172C19.0059 3.27498 22.3507 4.54664 24.9511 6.82934L25.4614 7.30072L26.1868 8.02638V5.01424C26.1868 4.26758 26.7915 3.66269 27.5379 3.66269C28.2843 3.66269 28.889 4.26758 28.889 5.01424V11.4238C28.8886 12.1701 28.2841 12.7754 27.5379 12.7754H21.1306C20.3848 12.7752 19.78 12.17 19.7795 11.4238C19.7795 10.6773 20.3844 10.0706 21.1306 10.0703H24.4075L23.5648 9.22733C21.4033 7.14596 18.5218 5.97642 15.521 5.96482C13.414 5.9656 11.3546 6.59142 9.60247 7.76232C7.84929 8.93419 6.48133 10.5999 5.67437 12.5485C4.86743 14.4974 4.65687 16.6427 5.06825 18.7117C5.4797 20.7804 6.49452 22.6812 7.98548 24.1728C8.7612 24.9487 9.64826 25.595 10.6114 26.0953C11.2737 26.4394 11.5319 27.2558 11.1882 27.9183C10.8442 28.5809 10.0283 28.8395 9.36588 28.4954C8.1585 27.8681 7.04768 27.0565 6.0752 26.0837C4.20636 24.214 2.93274 21.8328 2.41694 19.2398C1.90126 16.6463 2.16602 13.9567 3.17752 11.5138C4.18906 9.07114 5.9032 6.98391 8.10084 5.51496C10.2986 4.04609 12.8818 3.26172 15.5249 3.26172H15.5308Z"
        fill="currentColor"
      />
    </svg>
  );
});
ForwardTenIcon.displayName = "ForwardTenIcon";

export const EpisodeCheckedIcon = forwardRef<
  SVGSVGElement,
  AccessibleIconProps
>(({ title, "aria-hidden": ariaHidden = true, ...props }, ref) => {
  const resolvedTitle = title ?? "Episode complete";

  return (
    <svg
      ref={ref}
      width="24"
      height="25"
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={ariaHidden}
      role={props.role}
      {...props}
    >
      <title>{resolvedTitle}</title>
      <path
        d="M9 12.2961L11 14.2961L15 10.2961M21 12.2961C21 17.2667 16.9706 21.2961 12 21.2961C7.02944 21.2961 3 17.2667 3 12.2961C3 7.32558 7.02944 3.29614 12 3.29614C16.9706 3.29614 21 7.32558 21 12.2961Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
});
EpisodeCheckedIcon.displayName = "EpisodeCheckedIcon";

export const EpisodeUncheckedIcon = forwardRef<
  SVGSVGElement,
  AccessibleIconProps
>(({ title, "aria-hidden": ariaHidden = true, ...props }, ref) => {
  const resolvedTitle = title ?? "Episode incomplete";

  return (
    <svg
      ref={ref}
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={ariaHidden}
      role={props.role}
      {...props}
    >
      <title>{resolvedTitle}</title>
      <path
        d="M10 0.296143C4.47 0.296143 0 4.76614 0 10.2961C0 15.8261 4.47 20.2961 10 20.2961C15.53 20.2961 20 15.8261 20 10.2961C20 4.76614 15.53 0.296143 10 0.296143ZM10 18.2961C5.58 18.2961 2 14.7161 2 10.2961C2 5.87614 5.58 2.29614 10 2.29614C14.42 2.29614 18 5.87614 18 10.2961C18 14.7161 14.42 18.2961 10 18.2961Z"
        fill="currentColor"
      />
    </svg>
  );
});
EpisodeUncheckedIcon.displayName = "EpisodeUncheckedIcon";

export const PauseIcon = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
  ({ "aria-hidden": ariaHidden = true, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width="48"
        height="48"
        viewBox="0 0 48 48"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={ariaHidden}
        {...props}
      >
        <title>Pause</title>
        <path
          d="M8.40002 7.68078C8.40002 6.0902 9.70959 4.80078 11.325 4.80078L16.354 4.80078C17.9694 4.80078 19.279 6.0902 19.279 7.68078V40.3208C19.279 41.9114 17.9694 43.2008 16.354 43.2008H11.325C9.70959 43.2008 8.40002 41.9114 8.40002 40.3208V7.68078Z"
          fill="currentColor"
        />
        <path
          d="M28.7211 7.68078C28.7211 6.0902 30.0306 4.80078 31.6461 4.80078L36.675 4.80078C38.2905 4.80078 39.6 6.0902 39.6 7.68078V40.3208C39.6 41.9114 38.2905 43.2008 36.675 43.2008H31.6461C30.0306 43.2008 28.7211 41.9114 28.7211 40.3208V7.68078Z"
          fill="currentColor"
        />
      </svg>
    );
  },
);
PauseIcon.displayName = "PauseIcon";

export const ContinueIcon = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
  ({ "aria-hidden": ariaHidden = true, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width="20"
        height="21"
        viewBox="0 0 20 21"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={ariaHidden}
        {...props}
      >
        <title>Continue</title>
        <path
          d="M8.33332 14.1105L13.3333 10.3605L8.33332 6.61046V14.1105ZM9.99999 2.02713C5.39999 2.02713 1.66666 5.76046 1.66666 10.3605C1.66666 14.9605 5.39999 18.6938 9.99999 18.6938C14.6 18.6938 18.3333 14.9605 18.3333 10.3605C18.3333 5.76046 14.6 2.02713 9.99999 2.02713ZM9.99999 17.0271C6.32499 17.0271 3.33332 14.0355 3.33332 10.3605C3.33332 6.68546 6.32499 3.6938 9.99999 3.6938C13.675 3.6938 16.6667 6.68546 16.6667 10.3605C16.6667 14.0355 13.675 17.0271 9.99999 17.0271Z"
          fill="currentColor"
        />
      </svg>
    );
  },
);
ContinueIcon.displayName = "ContinueIcon";

export const VolumeHighIcon = forwardRef<SVGSVGElement, AccessibleIconProps>(
  ({ title, "aria-hidden": ariaHidden = true, ...props }, ref) => {
    const resolvedTitle = title ?? "Volume high";

    return (
      <HeroSpeakerWaveIcon
        ref={ref}
        aria-hidden={ariaHidden}
        role={props.role}
        {...props}
      >
        <title>{resolvedTitle}</title>
      </HeroSpeakerWaveIcon>
    );
  },
);
VolumeHighIcon.displayName = "VolumeHighIcon";

export const VolumeMediumIcon = forwardRef<SVGSVGElement, AccessibleIconProps>(
  ({ title, "aria-hidden": ariaHidden = true, ...props }, ref) => {
    const resolvedTitle = title ?? "Volume medium";

    return (
      <svg
        ref={ref}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={ariaHidden}
        role={props.role}
        {...props}
      >
        <title>{resolvedTitle}</title>
        <path
          d="M14.5534 3.37481C14.2496 3.20536 13.8774 3.21128 13.5792 3.39026L8.74821 6.28986H6.75262C5.45916 6.28986 4.41016 7.33886 4.41016 8.63231V15.3674C4.41016 16.6608 5.45916 17.7098 6.75262 17.7098H8.74821L13.5792 20.6094C13.8774 20.7884 14.2496 20.7943 14.5534 20.6249C14.8572 20.4554 15.0457 20.135 15.0457 19.7871V4.21258C15.0457 3.86465 14.8572 3.54426 14.5534 3.37481Z"
          fill="currentColor"
        />
        <path
          d="M17.1266 9.28648C17.4616 9.00588 17.96 9.05025 18.2406 9.38529C19.6283 11.0415 19.6283 13.9581 18.2406 15.6143C17.96 15.9494 17.4616 15.9937 17.1266 15.7131C16.7915 15.4325 16.7472 14.9341 17.0278 14.5991C17.9273 13.5254 17.9273 11.4741 17.0278 10.4005C16.7472 10.0655 16.7915 9.56708 17.1266 9.28648Z"
          fill="currentColor"
        />
      </svg>
    );
  },
);
VolumeMediumIcon.displayName = "VolumeMediumIcon";

export const VolumeLowIcon = forwardRef<SVGSVGElement, AccessibleIconProps>(
  ({ title, "aria-hidden": ariaHidden = true, ...props }, ref) => {
    const resolvedTitle = title ?? "Volume low";

    return (
      <svg
        ref={ref}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={ariaHidden}
        role={props.role}
        {...props}
      >
        <title>{resolvedTitle}</title>
        <path
          d="M14.5534 3.37481C14.2496 3.20536 13.8774 3.21128 13.5792 3.39026L8.74821 6.28986H6.75262C5.45916 6.28986 4.41016 7.33886 4.41016 8.63231V15.3674C4.41016 16.6608 5.45916 17.7098 6.75262 17.7098H8.74821L13.5792 20.6094C13.8774 20.7884 14.2496 20.7943 14.5534 20.6249C14.8572 20.4554 15.0457 20.135 15.0457 19.7871V4.21258C15.0457 3.86465 14.8572 3.54426 14.5534 3.37481Z"
          fill="currentColor"
        />
        <path
          d="M17.1162 10.6528C17.4594 10.3822 17.9571 10.4412 18.2277 10.7844C18.9241 11.6676 18.9241 12.932 18.2277 13.8152C17.9571 14.1584 17.4594 14.2174 17.1162 13.9468C16.773 13.6762 16.714 13.1785 16.9846 12.8353C17.2256 12.5298 17.2256 12.07 16.9846 11.7644C16.714 11.4212 16.773 10.9235 17.1162 10.6528Z"
          fill="currentColor"
        />
      </svg>
    );
  },
);
VolumeLowIcon.displayName = "VolumeLowIcon";

export const VolumeMutedIcon = forwardRef<SVGSVGElement, AccessibleIconProps>(
  ({ title, "aria-hidden": ariaHidden = true, ...props }, ref) => {
    const resolvedTitle = title ?? "Volume muted";

    return (
      <HeroSpeakerXMarkIcon
        ref={ref}
        aria-hidden={ariaHidden}
        role={props.role}
        {...props}
      >
        <title>{resolvedTitle}</title>
      </HeroSpeakerXMarkIcon>
    );
  },
);
VolumeMutedIcon.displayName = "VolumeMutedIcon";

export const PlayVideoIcon = forwardRef<SVGSVGElement, SVGProps<SVGSVGElement>>(
  ({ "aria-hidden": ariaHidden = true, ...props }, ref) => {
    return (
      <svg
        ref={ref}
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden={ariaHidden}
        {...props}
      >
        <title>Play video</title>
        <path
          d="M4 3.73205V20.268C4 21.0378 4.83333 21.5189 5.5 21.134L19.8205 12.866C20.4872 12.4811 20.4872 11.5189 19.8205 11.134L5.5 2.86603C4.83333 2.48113 4 2.96225 4 3.73205Z"
          fill="currentColor"
          stroke="currentColor"
        />
      </svg>
    );
  },
);
PlayVideoIcon.displayName = "PlayVideoIcon";

export const ListenAgainIcon = forwardRef<
  SVGSVGElement,
  SVGProps<SVGSVGElement>
>(({ "aria-hidden": ariaHidden = true, ...props }, ref) => {
  return (
    <svg
      ref={ref}
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={ariaHidden}
      {...props}
    >
      <title>Listen again</title>
      <path
        d="M10 5.68043V2.34709L5.83334 6.51376L10 10.6804V7.34709C12.7583 7.34709 15 9.58876 15 12.3471C15 15.1054 12.7583 17.3471 10 17.3471C7.24168 17.3471 5.00001 15.1054 5.00001 12.3471H3.33334C3.33334 16.0304 6.31668 19.0138 10 19.0138C13.6833 19.0138 16.6667 16.0304 16.6667 12.3471C16.6667 8.66376 13.6833 5.68043 10 5.68043Z"
        fill="currentColor"
      />
    </svg>
  );
});
ListenAgainIcon.displayName = "ListenAgainIcon";
